import useSWR, { mutate } from 'swr';
import axios from 'axios';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { SERVICES } from '@shared/constants/apiConfig';
import { getError } from '@shared/utils/helpers';
import { transformConsentData, tranformPreferences } from '../../shared';
import { ICoApplicant } from './components/CoApplicant/validation';
import { OptOutTypeItem, IPurpose, IConsentPreferencesDataResponse, IContactDetails } from './components/OptOutModal/interfaces';
import { IUpdateOCRData } from './interfaces';

const path = `/${SERVICES.CONTACT}/contacts`;

export const requestUpdateContact = (data: Partial<ICoApplicant>) => {
  return mutate(path, async () => {
    try {
      const resp = await axios.patch(`${path}/${data.id}`, data);
      Notistack.successMessage('Contact - Updated Successfully');
      mutate(`${path}/${data.id}/header`);

      return resp?.data;
    } catch (err) {
      const error = getError(err);
      Notistack.generalError(error);
      return { error };
    }
  });
};

export const requestCreateCoApplicant = (contactId: string, data: ICoApplicant) => {
  return mutate(path, async () => {
    try {
      const response = await axios.post(`${path}/${contactId}/co-applicants`, data);
      Notistack.successMessage('Co-Applicant - Added Successfully');
      return response?.data;
    } catch (err) {
      const error = getError(err);
      Notistack.generalError(error);
      return { error };
    }
  });
};

export const requestUpdateCoApplicant = (contactId: string, data: ICoApplicant) => {
  return mutate(path, async () => {
    try {
      const requestPath = `${path}/${contactId}/co-applicants/${data.coApplicantId}`;
      await axios.patch(requestPath, data);
      // const response = await axios.patch(requestPath, data);
      Notistack.successMessage('Co-Applicant - Updated Successfully');

      // mutate(`${path}/${contactId}`);
      // mutate(requestPath, response.data);
    } catch {
      Notistack.generalError();
    }
  });
};

export const requestDeleteCoApplicant = (contactId: string, coApplicantId: string) => {
  return mutate(path, async () => {
    try {
      await axios.delete(`${path}/${contactId}/co-applicants/${coApplicantId}`);
      Notistack.successMessage('Co-Applicant - Deleted Successfully');
      mutate(`${path}/${contactId}`);
    } catch (e) {
      Notistack.generalError();
      return e;
    }
  });
};

export const requestUpdateOptOut = (contactId: string, optOutId = '', items: OptOutTypeItem[]) => {
  const path = `/${SERVICES.CONSENT}/contacts`;
  return mutate(path, async () => {
    try {
      await axios.put(`${path}/${contactId}/optout/${optOutId}`, { optOutTypeItems: items });
      Notistack.successMessage('Opt-Out Preferences - Updated Successfully');
    } catch {
      Notistack.generalError();
    }
  });
};

export const requestUpdateOCRData = (data: IUpdateOCRData[]) => {
  const path = `${SERVICES.DOCUMENT}/ocrresult/update-contact-reviewed-data`;

  return mutate(path, async () => {
    try {
      await axios.post(path, data);
      Notistack.successMessage('OCR Data - Updated Successfully');
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const useGetConsentQuery = (contact: IContactDetails) => {
  const getConsentPath =
    contact?.email &&
    `${SERVICES.CONSENT}/consent/${contact?.email}?firstName=${contact?.firstName ?? ''}&lastName=${
      contact?.lastName ?? ''
    }&phone=${contact?.cellPhone ?? ''}`;
  const {
    data: consentPreferencesData,
    error,
    ...restSWR
  } = useSWR<IConsentPreferencesDataResponse>(getConsentPath, {
    onError: (error) => {
      const errorType = error?.response?.data.errorType;
      const errorStatus = error?.response?.status;
      if (errorType === 'EntityNotFound' && errorStatus === 404) {
        return;
      }
      Notistack.generalError();
    },
  });
  const transformedData: IPurpose[] = transformConsentData(consentPreferencesData?.purposes ?? []);
  const consentPreferencesTrasformedData = tranformPreferences(transformedData);

  return {
    ...restSWR,
    error,
    consentData: consentPreferencesTrasformedData,
  };
};

export const requestUpdateOneTrustOptIn = (identifier: string | null, items: IPurpose[], updateReloadFunction: () => void) => {
  if (identifier === null) {
    Notistack.generalError('Contact email is required.');
    return;
  }
  const path = `${SERVICES.CONSENT}/consent/${identifier}/preference`;
  return mutate(path, async () => {
    try {
      const updateOnetrustResponse = await axios.put(path, items);
      if (updateOnetrustResponse?.data?.isSuccess === true) {
        setTimeout(() => {
          Notistack.successMessage('Preferences - Updated Successfully');
          updateReloadFunction();
        }, 1000);
      }
    } catch {
      Notistack.generalError();
    }
  });
};
